import { Component } from '@theme/component';

/**
 * Product card of the in-stock-grid section.
 * Picking a color swaps the photo to that color's image (when it has one, otherwise the
 * product's default photo comes back), updates the stock badge and points the card link
 * at the first variant of that color. Per-color images are server-rendered into <template>s.
 *
 * @typedef {Object} Refs
 * @property {HTMLElement} image
 * @property {HTMLElement} badge
 * @property {HTMLAnchorElement} [link]
 *
 * @extends {Component<Refs>}
 */
class InStockCard extends Component {
  requiredRefs = ['image', 'badge'];

  /** @type {Node[]} */
  #defaultImage = [];

  connectedCallback() {
    super.connectedCallback();

    this.#defaultImage = [...this.refs.image.childNodes].map((node) => node.cloneNode(true));
    this.addEventListener('change', this.#handleChange);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.removeEventListener('change', this.#handleChange);
  }

  /**
   * @param {Event} event
   */
  #handleChange = (event) => {
    const input = event.target;
    if (!(input instanceof HTMLInputElement) || input.type !== 'radio') return;

    try {
      this.#updateImage(input.dataset.media);
      this.#updateBadge(input.dataset.label, input.dataset.tone);

      if (this.refs.link && input.dataset.url) this.refs.link.href = input.dataset.url;
    } catch (error) {
      throw new Error(`in-stock-card: could not switch to color "${input.value}"`, { cause: error });
    }
  };

  /**
   * @param {string | undefined} mediaId - id of the color's image template; empty restores the default photo
   */
  #updateImage(mediaId) {
    const template = mediaId ? this.querySelector(`template[data-media-for="${CSS.escape(mediaId)}"]`) : null;
    const nodes =
      template instanceof HTMLTemplateElement
        ? [template.content.cloneNode(true)]
        : this.#defaultImage.map((node) => node.cloneNode(true));

    this.refs.image.replaceChildren(...nodes);
  }

  /**
   * @param {string | undefined} label
   * @param {string | undefined} tone - ships | arriving | preorder | custom
   */
  #updateBadge(label, tone) {
    const { badge } = this.refs;

    badge.textContent = label ?? '';
    badge.hidden = !label;
    badge.className = `in-stock-card__badge in-stock-card__badge--${tone || 'ships'}`;
  }
}

if (!customElements.get('in-stock-card')) {
  customElements.define('in-stock-card', InStockCard);
}
